// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

// Backend Ad Types (matching Go structure)
export enum BackendAdType {
  Rewarded = 0,      // AdTypeRewarded
  Interstitial = 1,  // AdTypeInterstitial
  Banner = 2        // AdTypeBanner
}

// Virtual Currency (matching backend currency.VirtualCurrency)
export interface VirtualCurrency {
  type: number;      // Currency type (e.g., 1=Coin, 2=LudoCoin, etc.)
  amount: number;    // Currency amount
}

// Ad Unit ID (matching backend AdUnitID)
export interface AdUnitID {
  android: string;
  ios: string;
}

// Base Ad Item (matching backend AdItem)
export interface AdItem {
  enabled: boolean;
  type: BackendAdType;  // 0=Rewarded, 1=Interstitial, 2=Banner
  ad_unit_id: AdUnitID;
}

// Banner Ad (matching backend BannerAd)
export interface BannerAd extends AdItem {
  // BannerAd only extends AdItem, no additional fields
}

// Interstitial Ad (matching backend InterstitialAd)
export interface InterstitialAd extends AdItem {
  play_frequency: number;  // player after x matches
}

// Rewarded Ad (matching backend RewardedAd)
export interface RewardedAd extends AdItem {
  rewards: VirtualCurrency[];  // Array of virtual currency rewards
}

// App Keys (matching backend AppKeys)
export interface AppKeys {
  android: string;
  ios: string;
}

// Ads Config (matching backend AdsConfig)
export interface AdsConfig {
  app_keys: AppKeys;
  test_mode: boolean;
  rewarded: RewardedAd;
  interstitial: InterstitialAd;
  banner: BannerAd;
}

// Ad Network Types
export enum AdNetwork {
  ADMOB = 'admob',
  UNITY_ADS = 'unity_ads',
  FACEBOOK_AUDIENCE = 'facebook_audience',
  IRONSOURCE = 'ironsource',
  APPLOVIN = 'applovin'
}

export enum AdType {
  INTERSTITIAL = 'interstitial',
  REWARDED = 'rewarded',
  BANNER = 'banner',
  NATIVE = 'native'
}

export enum Platform {
  ANDROID = 'android',
  IOS = 'ios'
}

// Ad Network Configuration
export interface AdNetworkConfig {
  id: string;
  network: AdNetwork;
  name: string;
  enabled: boolean;
  priority: number; // 1 = highest priority
  platformConfigs: {
    [Platform.ANDROID]: PlatformAdConfig;
    [Platform.IOS]: PlatformAdConfig;
  };
  testMode: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformAdConfig {
  appId: string;
  adUnits: {
    [AdType.INTERSTITIAL]?: AdUnitConfig;
    [AdType.REWARDED]?: AdUnitConfig;
    [AdType.BANNER]?: AdUnitConfig;
    [AdType.NATIVE]?: AdUnitConfig;
  };
}

export interface AdUnitConfig {
  unitId: string;
  enabled: boolean;
  testUnitId?: string; // For testing purposes
}

// Ad Frequency Control
export interface AdFrequencyConfig {
  id: string;
  adType: AdType;
  enabled: boolean;
  rules: FrequencyRule[];
  createdAt: string;
  updatedAt: string;
}

export interface FrequencyRule {
  id: string;
  name: string;
  description: string;
  trigger: FrequencyTrigger;
  cooldown: number; // in seconds
  maxPerSession: number;
  maxPerDay: number;
  enabled: boolean;
}

export enum FrequencyTrigger {
  GAME_END = 'game_end',
  LEVEL_COMPLETE = 'level_complete',
  COINS_LOW = 'coins_low',
  LIFELINE_USED = 'lifeline_used',
  MANUAL_REQUEST = 'manual_request',
  TIME_INTERVAL = 'time_interval'
}

// Ad Revenue and Performance
export interface AdPerformanceMetrics {
  networkId: string;
  network: AdNetwork;
  platform: Platform;
  adType: AdType;
  impressions: number;
  clicks: number;
  revenue: number;
  fillRate: number; // percentage
  ctr: number; // click-through rate percentage
  ecpm: number; // effective cost per mille
  date: string;
}

export interface AdRevenueReport {
  totalRevenue: number;
  totalImpressions: number;
  totalClicks: number;
  averageCtr: number;
  averageEcpm: number;
  topPerformingNetwork: AdNetwork;
  performanceByNetwork: AdPerformanceMetrics[];
  performanceByType: AdPerformanceMetrics[];
  performanceByPlatform: AdPerformanceMetrics[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// Rewarded Ad Configuration
export interface RewardedAdConfig {
  id: string;
  name: string;
  description: string;
  rewardType: 'coins' | 'lifelines' | 'powerups' | 'custom';
  rewardAmount: number;
  enabled: boolean;
  availableGames: string[]; // game IDs where this reward is available
  cooldownMinutes: number;
  maxRewardsPerDay: number;
  createdAt: string;
  updatedAt: string;
}

// Ad Placement Configuration
export interface AdPlacementConfig {
  id: string;
  name: string;
  gameId: string;
  gameName: string;
  adType: AdType;
  placement: AdPlacement;
  enabled: boolean;
  frequency: number; // how often to show (e.g., every 3 games)
  priority: number;
  createdAt: string;
  updatedAt: string;
}

export enum AdPlacement {
  GAME_START = 'game_start',
  GAME_END = 'game_end',
  LEVEL_COMPLETE = 'level_complete',
  PAUSE_MENU = 'pause_menu',
  MAIN_MENU = 'main_menu',
  STORE = 'store',
  LEADERBOARD = 'leaderboard',
  SETTINGS = 'settings'
}

// API Request/Response Types
export interface CreateAdNetworkRequest {
  network: AdNetwork;
  name: string;
  enabled: boolean;
  priority: number;
  platformConfigs: {
    [Platform.ANDROID]: PlatformAdConfig;
    [Platform.IOS]: PlatformAdConfig;
  };
  testMode: boolean;
}

export interface UpdateAdNetworkRequest extends Partial<CreateAdNetworkRequest> {
  id: string;
}

export interface CreateFrequencyConfigRequest {
  adType: AdType;
  enabled: boolean;
  rules: Omit<FrequencyRule, 'id'>[];
}

export interface UpdateFrequencyConfigRequest extends Partial<CreateFrequencyConfigRequest> {
  id: string;
}

export interface CreateRewardedAdRequest {
  name: string;
  description: string;
  rewardType: 'coins' | 'lifelines' | 'powerups' | 'custom';
  rewardAmount: number;
  enabled: boolean;
  availableGames: string[];
  cooldownMinutes: number;
  maxRewardsPerDay: number;
}

export interface UpdateRewardedAdRequest extends Partial<CreateRewardedAdRequest> {
  id: string;
}

export interface CreateAdPlacementRequest {
  name: string;
  gameId: string;
  gameName: string;
  adType: AdType;
  placement: AdPlacement;
  enabled: boolean;
  frequency: number;
  priority: number;
}

export interface UpdateAdPlacementRequest extends Partial<CreateAdPlacementRequest> {
  id: string;
}

// List Response Types
export interface AdNetworkList {
  networks: AdNetworkConfig[];
  totalCount: number;
}

export interface FrequencyConfigList {
  configs: AdFrequencyConfig[];
  totalCount: number;
}

export interface RewardedAdList {
  rewards: RewardedAdConfig[];
  totalCount: number;
}

export interface AdPlacementList {
  placements: AdPlacementConfig[];
  totalCount: number;
}

// Ad Management Dashboard
export interface AdManagementDashboard {
  networks: AdNetworkConfig[];
  frequencyConfigs: AdFrequencyConfig[];
  rewardedAds: RewardedAdConfig[];
  placements: AdPlacementConfig[];
  revenueReport: AdRevenueReport;
  summary: {
    totalNetworks: number;
    activeNetworks: number;
    totalPlacements: number;
    activePlacements: number;
    totalRewardedAds: number;
    activeRewardedAds: number;
    dailyRevenue: number;
    dailyImpressions: number;
  };
}

// Search and Filter Types
export interface AdNetworkFilters {
  network?: AdNetwork;
  enabled?: boolean;
  platform?: Platform;
  testMode?: boolean;
}

export interface AdPerformanceFilters {
  network?: AdNetwork;
  adType?: AdType;
  platform?: Platform;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
}

export interface RewardedAdFilters {
  rewardType?: string;
  enabled?: boolean;
  gameId?: string;
}

export interface AdPlacementFilters {
  gameId?: string;
  adType?: AdType;
  placement?: AdPlacement;
  enabled?: boolean;
}

// Validation and Error Types
export interface AdConfigValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface AdNetworkTestResult {
  networkId: string;
  network: AdNetwork;
  platform: Platform;
  adType: AdType;
  success: boolean;
  message: string;
  testAdShown: boolean;
  responseTime: number;
}

// Export Types
export interface AdManagementExportOptions {
  format: 'excel' | 'pdf' | 'csv';
  includeNetworks: boolean;
  includePerformance: boolean;
  includeRewards: boolean;
  includePlacements: boolean;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}
